import { Module } from 'vuex';
import { api } from '../../plugins/salut-api';
import { AppBanner, AppBannerDto, LocaleCode } from '../../types';
import { RootState, AppState } from '../types';
import cloneDeep from 'lodash/cloneDeep';

const CHECK_VERSION_INTERVAL = 60 * 60 * 1000; // 1 hour
const VERSION_LOCALSTORAGE_KEY = 'version';
const VERSION_URL = '/version.txt';
const RESET_STATE = 'RESET_STATE';

export const AppActionKeys = {
	setUrlQueryLang: 'setUrlQueryLang',
	setPreviousPage: 'setPreviousPage',
	setRedirectTo: 'setRedirectTo',
	clearRedirectTo: 'clearRedirectTo',
	setBreadcrumbsItems: 'setBreadcrumbsItems',
	setVersionUpdate: 'setVersionUpdate',
	forceReloadIfVersionUpdated: 'forceReloadIfVersionUpdated',
	fetchAppBannerList: 'fetchAppBannerList',
	reorderAppBanners: 'reorderAppBanners',
	createAppBanner: 'createAppBanner',
	updateAppBanner: 'updateAppBanner',
	deleteAppBanner: 'deleteAppBanner',
	setCurrentAppBanner: 'setCurrentAppBanner',
	clearCurrentAppBanner: 'clearCurrentAppBanner',
	resetState: 'resetState',
};

export const AppGetterKeys = {
	lang: 'lang',
	preferenceLang: 'preferenceLang',
	urlQueryLang: 'urlQueryLang',
	previousPage: 'previousPage',
	redirectTo: 'redirectTo',
	breadcrumbsItems: 'breadcrumbsItems',
	previousBreadcrumbs: 'previousBreadcrumbs',
	versionUpdateDialog: 'versionUpdateDialog',
	loading: 'loading',
	lastError: 'lastError',
	appBannerList: 'appBannerList',
	currentAppBanner: 'currentAppBanner',
};

const SET_PREVIOUS_PAGE = 'SET_PREVIOUS_PAGE';
const SET_URL_QUERY_LANG = 'SET_URL_QUERY_LANG';
const SET_PREFERENCE_LANG = 'SET_PREFERENCE_LANG';
const CLEAR_URL_QUERY_LANG = 'CLEAR_URL_QUERY_LANG';
const SET_REDIRECT_TO = 'SET_REDIRECT_TO';
const SET_BREADCRUMBS_ITEMS = 'SET_BREADCRUMBS_ITEMS';
const SET_VERSION_UPDATE_DIALOG = 'SET_VERSION_UPDATE_DIALOG';
const SET_VERSION_CHECKED_TIMESTAMP = 'SET_VERSION_CHECKED_TIMESTAMP';
const SHOW_LOADING = 'SHOW_LOADING';
const HIDE_LOADING = 'HIDE_LOADING';
const SET_LAST_ERROR = 'SET_LAST_ERROR';
const SET_APP_BANNER_LIST = 'SET_APP_BANNER_LIST';
const SET_CURRENT_APP_BANNER = 'SET_CURRENT_APP_BANNER';
const CLEAR_CURRENT_APP_BANNER = 'CLEAR_CURRENT_APP_BANNER';

const initialState: AppState = {
	urlQueryLang: undefined,
	preferenceLang: undefined,
	previousPage: undefined,
	redirectTo: '/',
	breadcrumbsItems: [],
	versionUpdateDialog: false,
	isInitialVersionCheck: true,
	versionCheckedTimestamp: new Date(), //do not check right after page load, it is handled by isInitialVersionCheck already
	appBanners: null,
	currentAppBanner: null,
	loading: 0,
	lastError: '',
	previousBreadcrumbs: new Array(30),
};

const store: Module<AppState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	actions: {
		[AppActionKeys.setPreviousPage]({ commit }, { page }) {
			commit(SET_PREVIOUS_PAGE, page);
		},

		[AppActionKeys.setUrlQueryLang]({ commit }, { lang }) {
			commit(SET_URL_QUERY_LANG, lang);
		},

		setPreferenceLang({ commit }, { lang }) {
			commit(SET_PREFERENCE_LANG, lang);
		},

		clearUrlQueryLang({ commit }) {
			commit(CLEAR_URL_QUERY_LANG);
		},

		[AppActionKeys.setVersionUpdate]({ commit }) {
			commit(SET_VERSION_UPDATE_DIALOG);
		},

		[AppActionKeys.setRedirectTo]({ commit }, { path }) {
			commit(SET_REDIRECT_TO, path);
		},

		[AppActionKeys.clearRedirectTo]({ commit }) {
			commit(SET_REDIRECT_TO, undefined);
		},

		[AppActionKeys.setBreadcrumbsItems]({ commit }, { breadcrumbsItems }) {
			commit(SET_BREADCRUMBS_ITEMS, breadcrumbsItems);
		},

		async [AppActionKeys.forceReloadIfVersionUpdated]({ commit, state }) {
			const now = new Date();
			const ticks = now.getTime();
			if (
				ticks - state.versionCheckedTimestamp.getTime() >
				CHECK_VERSION_INTERVAL
			) {
				commit(SET_VERSION_CHECKED_TIMESTAMP, now);
				const response = await fetch(`${VERSION_URL}?_=${ticks}`, {
					cache: 'no-cache',
				});
				const version = await response.text();
				const prevVersion = localStorage.getItem(VERSION_LOCALSTORAGE_KEY);
				localStorage.setItem(VERSION_LOCALSTORAGE_KEY, version);

				if (prevVersion != null && prevVersion !== version) {
					// https://stackoverflow.com/questions/55127650/location-reloadtrue-is-deprecated
					// as reload(true) is deprecated, I just add timestamp in URL for forced reload
					await fetch(`/?_=${ticks}`, { cache: 'no-cache' });

					//popup dialog to prompt for refresh
					commit(SET_VERSION_UPDATE_DIALOG);
				}
			}
		},

		[AppActionKeys.setCurrentAppBanner](
			{ commit },
			{ appBanner }: { appBanner: AppBanner },
		) {
			const { center, ...banner } = appBanner;
			commit(SET_CURRENT_APP_BANNER, {
				...banner,
				// to meet BE class validator, need to remove keys with empty values
				jobId: appBanner.jobId || null,
				eventId: appBanner.eventId || null,
			} as AppBanner);
		},

		[AppActionKeys.clearCurrentAppBanner]({ commit }) {
			commit(CLEAR_CURRENT_APP_BANNER);
		},

		async [AppActionKeys.fetchAppBannerList]({ commit }) {
			try {
				commit(SHOW_LOADING);
				commit(SET_APP_BANNER_LIST, await api.contact.fetchAppBannerItems());
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [AppActionKeys.reorderAppBanners](
			{ commit },
			{ ids }: { ids: string[] },
		) {
			try {
				commit(SHOW_LOADING);
				const appBanner = await api.contact.reorderAppBannerItems(ids);
				commit(SET_CURRENT_APP_BANNER, appBanner);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [AppActionKeys.createAppBanner](
			{ commit },
			{ appBannerDto }: { appBannerDto: AppBannerDto },
		) {
			try {
				commit(SHOW_LOADING);
				const appBanner = await api.contact.createAppBannerItem(appBannerDto);
				commit(SET_CURRENT_APP_BANNER, appBanner);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [AppActionKeys.updateAppBanner](
			{ commit },
			{ id, appBannerDto }: { id: string; appBannerDto: AppBannerDto },
		) {
			try {
				commit(SHOW_LOADING);
				const appBanner = await api.contact.updateAppBannerItem(
					id,
					appBannerDto,
				);
				commit(SET_CURRENT_APP_BANNER, appBanner);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [AppActionKeys.deleteAppBanner]({ commit }, { id }: { id: string }) {
			try {
				commit(SHOW_LOADING);
				const appBanner = await api.contact.deleteAppBannerItem(id);
				commit(SET_CURRENT_APP_BANNER, appBanner);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [AppActionKeys.resetState]({ commit }) {
			commit(RESET_STATE);
		},
	},

	mutations: {
		[SET_PREVIOUS_PAGE](state, page) {
			state.previousPage = {
				...page,
				meta: { breadcrumbs: page.meta.breadcrumbs },
			};
			if (page.meta.breadcrumbs) {
				state.previousBreadcrumbs[page.meta.breadcrumbs.length - 1] =
					page.meta.breadcrumbs[page.meta.breadcrumbs.length - 1];
			}
		},
		[SET_URL_QUERY_LANG](state, lang) {
			state.urlQueryLang = lang;
		},

		[SET_PREFERENCE_LANG](state, lang) {
			state.preferenceLang = lang;
		},

		[CLEAR_URL_QUERY_LANG](state) {
			state.urlQueryLang = undefined;
		},

		[SET_REDIRECT_TO](state, path) {
			state.redirectTo = path;
		},

		[SET_BREADCRUMBS_ITEMS](state, items) {
			state.breadcrumbsItems = items;
		},

		[SET_VERSION_CHECKED_TIMESTAMP]: (state, value) => {
			state.versionCheckedTimestamp = value;
		},

		[SET_VERSION_UPDATE_DIALOG]: state => {
			state.versionUpdateDialog = true;
		},

		[SET_CURRENT_APP_BANNER](state, appBanner: AppBanner) {
			state.currentAppBanner = appBanner;
		},

		[CLEAR_CURRENT_APP_BANNER](state) {
			state.currentAppBanner = null;
		},

		[SET_APP_BANNER_LIST](state, appBanners: AppBanner[] | null) {
			state.appBanners = appBanners;
		},

		[SHOW_LOADING](state) {
			state.loading += 1;
		},

		[HIDE_LOADING](state) {
			state.loading -= 1;
		},

		[SET_LAST_ERROR](state, error) {
			state.lastError = error;
		},

		[RESET_STATE](state) {
			Object.assign(state, cloneDeep(initialState));
		},
	},

	getters: {
		[AppGetterKeys.lang]: state =>
			state.urlQueryLang || state.preferenceLang || LocaleCode.EN, // determine which param can override which
		[AppGetterKeys.preferenceLang]: state => state.preferenceLang,
		[AppGetterKeys.urlQueryLang]: state => state.urlQueryLang,
		[AppGetterKeys.previousPage]: state => state.previousPage,
		[AppGetterKeys.redirectTo]: state => state.redirectTo,
		[AppGetterKeys.breadcrumbsItems]: state => state.breadcrumbsItems,
		[AppGetterKeys.previousBreadcrumbs]: state => state.previousBreadcrumbs,
		[AppGetterKeys.versionUpdateDialog]: state => state.versionUpdateDialog,

		[AppGetterKeys.loading]: state => Boolean(state.loading),
		[AppGetterKeys.lastError]: state => state.lastError,
		[AppGetterKeys.appBannerList]: state => state.appBanners,
		[AppGetterKeys.currentAppBanner]: state => state.currentAppBanner,
	},
};

export default store;
